import { CubePresenter, FlipIntent, Logger, Presenter } from 'wdc-cube'

import { MainPresenter } from 'src/main/Main.presenter'

import AssessmentIcon from '@mui/icons-material/Assessment'
import HubIcon from '@mui/icons-material/Hub'
import PetsIcon from '@mui/icons-material/Pets'
import DatabaseIcon from 'src/components/icons/DatabaseIcon'
import StethoscopeIcon from 'src/components/icons/StethoscopeIcon'
import { AppStorage } from 'src/utils'
import { MercadoVetKeys } from '../mercado-vet/mv_keys'
import { AnaliseDesempenhoKeys } from '../analise-desempenho/ad_keys'
import { TheActingKeys } from '../the-acting/ta_keys'
import { TheConnectionKeys } from '../the-connection/tc_keys'
import { TheConsumerMarketKeys } from '../the-consumer-market/tcm_keys'
import OverviewKeys from './overview_keys'
import { EnumModuleNames, HeaderScope, ModuleCard, OverviewScope } from './overview_scopes'

const appStorage = AppStorage.singleton()
const LOG = Logger.get('OverviewPresenter')

export class OverviewPresenter extends CubePresenter<MainPresenter, OverviewScope> {
    // :: Fields

    private readonly __headerBar: Presenter<HeaderScope, OverviewPresenter>

    // :: Constructor

    public constructor(app: MainPresenter) {
        super(app, new OverviewScope())
        this.__headerBar = new Presenter(this, this.scope.header)
    }

    // :: Methods

    public override release() {
        this.app.releaseToolbar(this.__headerBar)
        super.release()
        LOG.debug('Finalized')
    }

    public override async applyParameters(intent: FlipIntent, initialization: boolean): Promise<boolean> {
        const keys = new OverviewKeys(this.app, intent)

        this.app.scope.toolbar = this.__headerBar.scope

        keys.parentSlot(this.scope)

        if (initialization) {
            await this.#initialize()
        }

        return keys.allow
    }

    async #initialize() {
        LOG.debug('initialize')
        this.scope.onOpenModule = this.action(this.handleOpenModule)
        this.scope.modules = this.getModules()
    }

    private async handleOpenModule(moduleName: EnumModuleNames) {
        switch (moduleName) {
            case EnumModuleNames.THE_CONNECTION:
                {
                    const key = new TheConnectionKeys(this.app)
                    await key.flip()
                }
                break
            case EnumModuleNames.THE_CONSUMER_MARKET:
                {
                    const key = new TheConsumerMarketKeys(this.app)
                    await key.flip()
                }
                break
            case EnumModuleNames.THE_ACTING:
                {
                    const key = new TheActingKeys(this.app)
                    await key.flip()
                }
                break
            case EnumModuleNames.MERCADO_VET:
                {
                    const key = new MercadoVetKeys(this.app)
                    await key.flip()
                }
                break
            case EnumModuleNames.ANALISE_DESEMPENHO:
                {
                    const key = new AnaliseDesempenhoKeys(this.app)
                    await key.flip()
                }
                break
        }
    }

    private getModules(): ModuleCard[] {
        return [
            {
                icon: DatabaseIcon,
                moduleName: EnumModuleNames.THE_CONSUMER_MARKET,
                name: 'Mercado IA',
                description: 'Panorama completo do cenário empresarial brasileiro para inteligência competitiva.',
                active: appStorage.theConsumerMarketEnabled,
                bullets: [
                    'Todas as empresas do Brasil mapeadas',
                    'Características detalhadas (Porte, Natureza, CNAE)',
                    'Segmentação avançada de mercado'
                ]
            },
            {
                icon: HubIcon,
                moduleName: EnumModuleNames.THE_CONNECTION,
                active: appStorage.theConnectionEnabled,
                name: 'Relações Societárias',
                description: 'Estruturas societárias, participações e conexões entre sócios e empresas.',
                bullets: [
                    'Grafo de conexões e participações',
                    'Vínculos cruzados entre sócios',
                    'Estruturas societárias completas'
                ]
            },
            {
                icon: StethoscopeIcon,
                moduleName: EnumModuleNames.THE_ACTING,
                active: appStorage.theActingEnabled,
                name: 'Profissionais Médicos',
                description:
                    'Segmentação por especialidade, área de atuação, procedimentos realizados e geolocalização.',
                bullets: [
                    'Mapeamento de empresas, lotação e qualificação societária',
                    'Local de lotação e ambiente de trabalho',
                    'Especialidades médicas por profissional'
                ]
            },
            {
                icon: PetsIcon,
                moduleName: EnumModuleNames.MERCADO_VET,
                active: appStorage.mercadoVetEnabled,
                name: 'Mercado Veterinário',
                description: 'Clínicas e hospitais veterinários do Brasil, com presença na internet e no Google Maps.',
                bullets: [
                    'Empresas com atividade veterinária e seus contatos',
                    'Estabelecimentos do Google Maps com foto e avaliação',
                    'Mapa, atividades secundárias e evolução do mercado'
                ]
            },
            {
                icon: AssessmentIcon,
                moduleName: EnumModuleNames.ANALISE_DESEMPENHO,
                active: appStorage.analiseDesempenhoEnabled,
                name: 'Análise de Desempenho',
                description: 'Comparativo de operadoras de planos de saúde com os dados da ANS.',
                bullets: [
                    'Beneficiários, market share e perfil da carteira',
                    'Receitas, despesas e sinistralidade',
                    'Índices de liquidez e evolução trimestral'
                ]
            }
        ] as ModuleCard[]
    }
}

export default OverviewPresenter
